import { format, parse, isToday } from "date-fns";
import { fr } from "date-fns/locale";
import { Card } from "../ui/card";
import { Badge } from "../ui/badge";
import { getTransactionTypeLabel } from "./TransactionTypeUtils";
import { MediaPreviewDialog } from "./MediaPreviewDialog";
import { useState } from "react";
import { Eye, BadgeEuro, HelpCircle } from "lucide-react";
import { Button } from "../ui/button";
import { TransactionSignature } from "./TransactionSignature";
import { SaleValidationDialog } from "../validation/SaleValidationDialog";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useQuery } from "@tanstack/react-query";
import { useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useOwnerCurrency } from "@/hooks/useOwnerCurrency";

interface TransactionGroupProps {
  date: string;
  transactions: any[];
  total: number;
  transactionType?: string;
}

export const TransactionGroup = ({
  date,
  transactions,
  total,
  transactionType
}: TransactionGroupProps) => {
  const [selectedProofUrl, setSelectedProofUrl] = useState<string | null>(null);
  const [isValidationOpen, setIsValidationOpen] = useState(false);
  const [isAddingToRevenue, setIsAddingToRevenue] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isHelpDialogOpen, setIsHelpDialogOpen] = useState(false);
  const [countedAmount, setCountedAmount] = useState("");
  const [explanation, setExplanation] = useState("");
  const { data: currency } = useOwnerCurrency();
  
  const allTransactionsValidated = transactions.every(t => t.is_deducted_from_revenue);
  const queryClient = useQueryClient();

  const parseDate = (dateStr: string) => {
    try {
      const parsedDate = parse(dateStr, 'd MMM yyyy', new Date(), {
        locale: fr
      });
      return format(parsedDate, 'yyyy-MM-dd');
    } catch (error) {
      console.error("Erreur lors du parsing de la date:", error);
      return null;
    }
  };

  const formatCurrency = (amount: number) => {
    return `${amount.toLocaleString()} ${currency || 'FCFA'}`;
  };

  const {
    data: discrepancy,
    refetch
  } = useQuery({
    queryKey: ["discrepancy", date],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Non authentifié");

      const formattedDate = parseDate(date);
      if (!formattedDate) throw new Error("Format de date invalide");

      const { data, error } = await supabase
        .from("counting_discrepancies")
        .select("*")
        .eq("created_by", user.id)
        .eq("counting_date", formattedDate)
        .maybeSingle();

      console.log("Fetched discrepancy:", data);
      if (error) throw error;
      return data;
    }
  });

  const handleValidateAsRevenue = async () => {
    if (!discrepancy) return;
    try {
      setIsAddingToRevenue(true);
      const {
        error
      } = await supabase.from('poultry_transactions').update({
        is_deducted_from_revenue: true
      }).in('id', transactions.map(t => t.id));
      if (error) throw error;
      const {
        error: discrepancyError
      } = await supabase.from('counting_discrepancies').update({
        status: 'validated_as_revenue'
      }).eq('id', discrepancy.id);
      if (discrepancyError) throw discrepancyError;
      toast.success("Transactions ajoutées au chiffre d'affaires");
      await refetch();
      await queryClient.invalidateQueries({
        queryKey: ["transactions"]
      });
      await queryClient.invalidateQueries({
        queryKey: ["validatedAmounts"]
      });
      await queryClient.invalidateQueries({
        queryKey: ["transactions_summary"]
      });
      await queryClient.invalidateQueries({
        queryKey: ["dashboard_stats"]
      });
    } catch (error) {
      console.error("Erreur lors de l'ajout au chiffre d'affaires:", error);
      toast.error("Une erreur s'est produite");
    } finally {
      setIsAddingToRevenue(false);
    }
  };

  const handleAddToRevenue = async () => {
    try {
      setIsAddingToRevenue(true);
      const {
        error
      } = await supabase.from('poultry_transactions').update({
        is_deducted_from_revenue: true
      }).in('id', transactions.map(t => t.id));
      if (error) throw error;
      toast.success("Transactions ajoutées au chiffre d'affaires");
      await queryClient.invalidateQueries({
        queryKey: ["transactions"]
      });
      await queryClient.invalidateQueries({
        queryKey: ["validatedAmounts"]
      });
      await queryClient.invalidateQueries({
        queryKey: ["transactions_summary"]
      });
      await queryClient.invalidateQueries({
        queryKey: ["dashboard_stats"]
      });
    } catch (error) {
      console.error("Erreur lors de l'ajout au chiffre d'affaires:", error);
      toast.error("Une erreur s'est produite");
    } finally {
      setIsAddingToRevenue(false);
    }
  };

  const calculateTotal = () => {
    console.log("Calculating total with discrepancy:", discrepancy);
    if (discrepancy) {
      if (discrepancy.status === 'salary_deduction' || discrepancy.status === 'forgiven' || discrepancy.status === 'justified' || discrepancy.status === 'validated_as_revenue') {
        console.log("Using counted amount:", discrepancy.counted_amount);
        return discrepancy.counted_amount;
      }
    }
    const total = transactions.reduce((total, transaction) => {
      if (transaction.transaction_type === 'sale') {
        return total + transaction.total_amount;
      } else if (transaction.transaction_type === 'return') {
        return total - transaction.refund_amount;
      }
      return total;
    }, 0);
    console.log("Using transactions total:", total);
    return total;
  };

  const totalAmount = calculateTotal();
  const handleSubmitDifference = async () => {
    if (!countedAmount || !explanation) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }
    try {
      const {
        data: {
          user
        }
      } = await supabase.auth.getUser();
      if (!user) throw new Error("Non authentifié");
      const counted = parseFloat(countedAmount);
      const expectedAmount = totalAmount;
      const difference = counted - expectedAmount;
      const formattedDate = parseDate(date);
      if (!formattedDate) {
        toast.error("Format de date invalide");
        return;
      }
      const {
        error
      } = await supabase.from("counting_discrepancies").insert({
        counting_date: formattedDate,
        payment_method: 'cash',
        expected_amount: expectedAmount,
        counted_amount: counted,
        difference_amount: difference,
        explanation,
        created_by: user.id,
        status: 'pending'
      });
      if (error) throw error;
      toast.success("Différence enregistrée avec succès");
      setIsDialogOpen(false);
      setCountedAmount("");
      setExplanation("");
      refetch();
    } catch (error) {
      console.error("Erreur lors de l'enregistrement de la différence:", error);
      toast.error("Une erreur est survenue");
    }
  };

  const isCurrentDay = () => {
    try {
      const parsedDate = parse(date, 'd MMM yyyy', new Date(), {
        locale: fr
      });
      return isToday(parsedDate);
    } catch (error) {
      console.error("Erreur lors du parsing de la date:", error);
      return false;
    }
  };

  const renderDiscrepancyStatus = () => {
    if (!discrepancy) return null;
    switch (discrepancy.status) {
      case 'pending':
        return <div className="text-yellow-600 font-medium">
            Une vérification est en cours
          </div>;
      case 'salary_deduction':
        return <Badge variant="destructive">
            Retenu sur salaire
          </Badge>;
      case 'justified':
        return <Badge variant="outline" className="bg-green-500 text-white">
            Différence justifiée
          </Badge>;
      case 'forgiven':
        return <Badge variant="outline" className="border-green-500 text-green-600">
            Différence pardonnée
          </Badge>;
      default:
        return null;
    }
  };

  const showActionButtons = !discrepancy || discrepancy.status !== 'pending' && !allTransactionsValidated;
  const isDifferenceButtonVisible = () => {
    return !allTransactionsValidated && (!discrepancy || discrepancy.status !== 'pending');
  };

  const isDifferenceButtonDisabled = () => {
    return !!discrepancy || isCurrentDay();
  };

  const getDifferenceButtonTooltip = () => {
    if (discrepancy) {
      return "Une différence a déjà été déclarée pour cette journée";
    }
    if (isCurrentDay()) {
      return "La déclaration de différence sera possible à partir de minuit";
    }
    return "";
  };

  const isRevenueButtonDisabled = () => {
    return isCurrentDay();
  };

  const getRevenueButtonTooltip = () => {
    if (isCurrentDay()) {
      return "La validation comme chiffre d'affaires sera possible à partir de minuit";
    }
    return "";
  };

  const showValidateAmountButton = discrepancy && (discrepancy.status === 'salary_deduction' || discrepancy.status === 'justified' || discrepancy.status === 'forgiven') && !allTransactionsValidated;

  return <Card className="p-4 mb-4">
      <div className="font-medium text-gray-600 mb-4 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div className="flex flex-col gap-2 w-full">
          <div className="flex items-center gap-2">
            <span>{date}</span>
            <Button variant="ghost" onClick={() => setIsHelpDialogOpen(true)} className="h-6 flex items-center gap-1 px-2 hover:bg-accent text-slate-950">
              <HelpCircle className="h-4 w-4" />
              <span className="text-lg text-left font-semibold text-purple-500">Comment ça fonctionne</span>
            </Button>
          </div>
          <div className="text-lg font-semibold text-green-600">
            Total: {formatCurrency(total)}
          </div>
          {discrepancy && <div className="text-lg font-semibold text-blue-600">
              Montant compté: {formatCurrency(discrepancy.counted_amount)}
            </div>}
          {renderDiscrepancyStatus()}
        </div>
        <div className="flex flex-wrap gap-2">
          {isDifferenceButtonVisible() && <div className="relative group">
              <Button onClick={() => setIsDialogOpen(true)} className="flex items-center gap-2 w-full md:w-auto" disabled={isDifferenceButtonDisabled()}>
                Déclarer une différence
              </Button>
              {isDifferenceButtonDisabled() && <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 hidden group-hover:block bg-black/90 text-white text-sm rounded-md px-3 py-2 whitespace-nowrap z-50 shadow-lg">
                  {getDifferenceButtonTooltip()}
                  <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-black/90 rotate-45"></div>
                </div>}
            </div>}
          {showValidateAmountButton && <Button variant="default" onClick={handleValidateAsRevenue} disabled={isRevenueButtonDisabled()} className="flex items-center gap-2 w-full md:w-auto">
              <BadgeEuro className="h-4 w-4" />
              Valider montant compté comme CA
            </Button>}
          {(!discrepancy || discrepancy.status === 'validated_as_revenue') && !allTransactionsValidated && <div className="relative group w-full md:w-auto">
              <Button variant="outline" onClick={handleAddToRevenue} disabled={isRevenueButtonDisabled()} className="flex items-center gap-2 w-full md:w-auto bg-emerald-500 hover:bg-emerald-400 text-gray-950">
                <BadgeEuro className="h-4 w-4" />
                Valider comme chiffre d'affaires
              </Button>
              {isRevenueButtonDisabled() && <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 hidden group-hover:block bg-black/90 text-white text-sm rounded-md px-3 py-2 whitespace-nowrap z-50 shadow-lg">
                  {getRevenueButtonTooltip()}
                  <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-black/90 rotate-45"></div>
                </div>}
            </div>}
        </div>
      </div>

      <div className="space-y-4">
        {transactions.map(transaction => {
          const isLoss = transaction.transaction_type === "loss";
          const isRestock = transaction.transaction_type === "restock";
          const isCredit = !transaction.is_fully_paid;
          const isReturn = transaction.transaction_type === "return";
          const isRefund = isReturn && transaction.refund_amount;

          return <div key={transaction.id} className={cn("border-b last:border-b-0 pb-4", isCredit && "bg-orange-50 p-4 rounded-lg border border-orange-200", isRefund && "bg-red-50 p-4 rounded-lg border border-red-200")}>
              <div className="flex flex-col md:flex-row justify-between items-start gap-2 mb-2">
                <div className="flex flex-col md:flex-row items-start md:items-center gap-2">
                  <span className="text-sm text-gray-600">
                    {format(new Date(transaction.created_at), "HH:mm", {
                      locale: fr
                    })}
                  </span>
                  <Badge variant={isLoss ? "destructive" : isRestock ? "secondary" : isCredit ? "default" : isRefund ? "destructive" : "secondary"} className={cn(isCredit && "bg-orange-500 hover:bg-orange-600", isRefund && "bg-red-500 hover:bg-red-600")}>
                    {getTransactionTypeLabel(transaction.transaction_type)}
                    {isCredit && " (Crédit)"}
                    {isRefund && " (Remboursement)"}
                  </Badge>
                  {transaction.is_deducted_from_revenue && <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                      Validé CA
                    </Badge>}
                </div>
                <span className={cn("font-medium text-right", isCredit && "text-orange-600", isRefund && "text-red-600")}>
                  {isRefund ? formatCurrency(-transaction.refund_amount) : isCredit ? <div className="flex flex-col items-end">
                        <span>{formatCurrency(transaction.paid_amount)}</span>
                        <span className="text-sm text-gray-500">
                          (Total: {formatCurrency(transaction.total_amount)})
                        </span>
                      </div> : formatCurrency(transaction.total_amount)}
                </span>
              </div>
              
              {transaction.stock_items && <div className="text-sm text-gray-600 mb-1">
                  {transaction.stock_items.data.name}
                </div>}
              
              {transaction.notes && <div className="text-sm text-gray-500 mb-1">
                  {isLoss ? <span className="text-red-500">
                      Raison de la perte: {transaction.notes}
                    </span> : isRestock ? <span className="text-green-600">
                      Détails du réapprovisionnement: {transaction.notes}
                    </span> : isRefund ? <span className="text-red-500">
                      Raison du remboursement: {transaction.notes}
                    </span> : transaction.notes}
                </div>}

              <div className="mt-2 space-y-2">
                {transaction.seller_signature && <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">Signature du vendeur:</span>
                    <TransactionSignature signature={transaction.seller_signature} />
                  </div>}

                {transaction.digital_signature && <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">Signature du client:</span>
                    <TransactionSignature signature={transaction.digital_signature} />
                  </div>}
              </div>

              {isLoss && transaction.proof_url && <div className="mt-2">
                  <Button variant="outline" size="sm" className="flex items-center gap-2 text-blue-600 hover:text-blue-700 w-full md:w-auto" onClick={() => setSelectedProofUrl(transaction.proof_url)}>
                    <Eye className="h-4 w-4" />
                    Voir la preuve
                  </Button>
                </div>}
            </div>;
        })}
      </div>

      <MediaPreviewDialog open={!!selectedProofUrl} onOpenChange={() => setSelectedProofUrl(null)} url={selectedProofUrl || ""} />

      <SaleValidationDialog isOpen={isValidationOpen} onClose={() => setIsValidationOpen(false)} expectedAmount={total} onValidate={() => {
      setIsValidationOpen(false);
    }} defaultDate={new Date(date)} />

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Déclarer une différence</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <div className="font-medium">Montant attendu:</div>
              <div className="text-lg font-bold">{formatCurrency(total)}</div>
            </div>
            <div className="space-y-2">
              <label htmlFor="counted" className="font-medium">Montant compté</label>
              <Input id="counted" type="number" value={countedAmount} onChange={e => setCountedAmount(e.target.value)} placeholder="Entrez le montant compté" />
            </div>
            <div className="space-y-2">
              <label htmlFor="explanation" className="font-medium">Explication <span className="text-red-500">*</span></label>
              <Textarea id="explanation" value={explanation} onChange={e => setExplanation(e.target.value)} placeholder="Expliquez la raison de la différence" required />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setIsDialogOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleSubmitDifference}>
              Envoyer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isHelpDialogOpen} onOpenChange={setIsHelpDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Guide des actions disponibles</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-muted p-4 rounded-lg">
              <h3 className="font-semibold mb-2">Comprendre les montants</h3>
              <ul className="list-disc list-inside space-y-2 text-sm text-muted-foreground">
                <li>
                  <span className="font-medium">Montant attendu :</span> Il s'agit de la somme totale des transactions de la journée (ventes - remboursements). C'est le montant qui devrait être présent dans votre caisse.
                </li>
                <li>
                  <span className="font-medium">Montant compté :</span> C'est le montant réel que vous avez compté physiquement dans votre caisse. Il est crucial de compter l'argent de chaque journée pour avoir une vision exacte de vos gains réels.
                </li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold mb-2">1. Déclarer une différence</h3>
              <p className="text-sm text-muted-foreground">
                À utiliser quand le montant que vous avez physiquement compté ne correspond pas au montant attendu. 
                Cette action est essentielle pour :
              </p>
              <ul className="list-disc list-inside mt-2 text-sm text-muted-foreground">
                <li>Assurer la transparence des comptes</li>
                <li>Identifier rapidement les écarts de caisse</li>
                <li>Permettre un suivi précis de votre chiffre d'affaires réel</li>
              </ul>
              <p className="text-sm text-muted-foreground mt-2">
                Une fois déclarée, la différence apparaîtra dans l'onglet "Personnel" où un responsable pourra :
              </p>
              <ul className="list-disc list-inside mt-2 text-sm text-muted-foreground">
                <li>Justifier la différence avec une explication</li>
                <li>Appliquer une retenue sur salaire si nécessaire</li>
                <li>Pardonner la différence si justifiée</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold mb-2">2. Valider montant compté comme CA</h3>
              <p className="text-sm text-muted-foreground">
                Cette option apparaît après qu'une différence a été traitée. En cliquant, vous :
              </p>
              <ul className="list-disc list-inside mt-2 text-sm text-muted-foreground">
                <li>Validez définitivement le montant réellement compté</li>
                <li>L'ajoutez au chiffre d'affaires officiel</li>
                <li>Clôturez le traitement de la différence</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold mb-2">3. Valider comme chiffre d'affaires</h3>
              <p className="text-sm text-muted-foreground">
                À utiliser uniquement quand le montant compté correspond exactement au montant attendu. Cette action :
              </p>
              <ul className="list-disc list-inside mt-2 text-sm text-muted-foreground">
                <li>Valide directement le montant des transactions</li>
                <li>L'ajoute au chiffre d'affaires</li>
                <li>Ne nécessite pas de validation supplémentaire</li>
              </ul>
            </div>

            <div className="bg-yellow-50 p-4 rounded-lg mt-4 border border-yellow-200">
              <p className="text-sm font-medium text-yellow-800">Important :</p>
              <ul className="list-disc list-inside mt-2 text-sm text-yellow-700">
                <li>Il est essentiel de compter physiquement l'argent chaque jour</li>
                <li>Ces actions ne sont disponibles qu'à partir du lendemain</li>
                <li>Une fois validé, le montant ne peut plus être modifié</li>
                <li>Toute différence doit être expliquée avant validation</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setIsHelpDialogOpen(false)}>J'ai compris</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>;
};
